import { useEffect, useState, useMemo } from "react";
import Slider from "react-slick";
import type { RootState } from "../store/store";
import { useSelector } from "react-redux";
import VlRangeSelector from "../components/elements/calendar/vl-range-selector";

interface VehicleListProps {
  special?: string; // ID des speziellen Fahrzeugs
}

interface FilterState {
  type: string[];
  transmission: string[];
  persons: string[];
  doors: string[];
  fuelType: string[];
}

function VehicleList({ special }: VehicleListProps) {
  const settings = {
    dots: true,
    fade: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    waitForAnimate: false,
  };

  const reduxVehicles = useSelector(
    (state: RootState) => state.vehicles.vehicles,
  );

  const [filters, setFilters] = useState<FilterState>({
    type: [],
    transmission: [],
    persons: [],
    doors: [],
    fuelType: [],
  });

  // Extrahiere einzigartige Filterwerte aus den Fahrzeugen
  const filterOptions = useMemo(() => {
    const types = new Set<string>();
    const transmissions = new Set<string>();
    const persons = new Set<string>();
    const doors = new Set<string>();
    const fuelTypes = new Set<string>();

    reduxVehicles.forEach((vehicle: any) => {
      if (vehicle.vehicle_category[0].id)
        types.add(vehicle.vehicle_category[0].id);

      vehicle.features?.forEach((feature: any) => {
        switch (feature.feature.feature_name) {
          case "Gangschaltung":
            transmissions.add(feature.value);
            break;
          case "Personen":
            persons.add(feature.value);
            break;
          case "Türen":
            doors.add(feature.value);
            break;
        }
      });
    });

    return {
      types: Array.from(types).sort(),
      transmissions: Array.from(transmissions).sort(),
      persons: Array.from(persons).sort((a, b) => Number(a) - Number(b)),
      doors: Array.from(doors).sort((a, b) => Number(a) - Number(b)),
      fuelTypes: Array.from(fuelTypes).sort(),
    };
  }, [reduxVehicles]);

  // Filtere und sortiere Fahrzeuge
  const filteredVehicles = useMemo(() => {
    let filtered = reduxVehicles.filter((vehicle: any) => {
      // Type Filter
      const vehicleCategory = vehicle.vehicle_category || [];
      if (filters.type.length > 0) {
        let hasType = false;
        for (const key in vehicleCategory) {
          if (!Object.hasOwn(vehicleCategory, key)) continue;
          const element = vehicleCategory[key];
          if (filters.type.includes(element.id)) {
            hasType = true;
          }
        }
        if (hasType) return true;
        return false;
      }
      // if(!filters.transmission.includes(transmission.value))

      // Feature-basierte Filter
      const vehicleFeatures = vehicle.features || [];

      // Gangschaltung Filter
      if (filters.transmission.length > 0) {
        const transmission = vehicleFeatures.find(
          (f: any) => f.feature.feature_name === "Gangschaltung",
        );
        if (
          !transmission ||
          !filters.transmission.includes(transmission.value)
        ) {
          return false;
        }
      }

      // Personen Filter
      if (filters.persons.length > 0) {
        const persons = vehicleFeatures.find(
          (f: any) => f.feature.feature_name === "Personen",
        );
        if (!persons || !filters.persons.includes(persons.value)) {
          return false;
        }
      }

      // Türen Filter
      if (filters.doors.length > 0) {
        const doors = vehicleFeatures.find(
          (f: any) => f.feature.feature_name === "Türen",
        );
        if (!doors || !filters.doors.includes(doors.value)) {
          return false;
        }
      }

      return true;
    });

    // Sortiere: Special-Fahrzeug zuerst
    if (special) {
      const specialIndex = filtered.findIndex((v: any) => v.id === special);
      if (specialIndex > -1) {
        const specialVehicle = filtered.splice(specialIndex, 1)[0];
        filtered.unshift(specialVehicle);
      }
    }

    return filtered;
  }, [reduxVehicles, filters, special]);

  const handleFilterChange = (
    filterType: keyof FilterState,
    value: string,
    checked: boolean,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: checked
        ? [...prev[filterType], value]
        : prev[filterType].filter((v) => v !== value),
    }));
  };

  const resetFilters = () => {
    setFilters({
      type: [],
      transmission: [],
      persons: [],
      doors: [],
      fuelType: [],
    });
  };

  const activeFilterCount = Object.values(filters).reduce(
    (sum, arr) => sum + arr.length,
    0,
  );

  return (
    <div className="orm-html orm-vehicleList-wrapper">
      {/* Filter Section */}
      <aside className="orm-vehicleList-filters" aria-label="Fahrzeugfilter">
        <div className="orm-filters-header">
          <h2>Filter</h2>
          {activeFilterCount > 0 && (
            <button
              onClick={resetFilters}
              className="orm-filters-reset"
              aria-label="Alle Filter zurücksetzen"
            >
              Zurücksetzen ({activeFilterCount})
            </button>
          )}
        </div>

        {/* Fahrzeugtyp Filter */}
        {filterOptions.types.length > 0 && (
          <fieldset className="orm-filter-group">
            <legend>Fahrzeugtyp</legend>
            {filterOptions.types.map((type) => (
              <label key={type} className="orm-filter-option">
                <input
                  type="checkbox"
                  checked={filters.type.includes(type)}
                  onChange={(e) =>
                    handleFilterChange("type", type, e.target.checked)
                  }
                  aria-label={`Filter nach ${type}`}
                />
                <span>{type}</span>
              </label>
            ))}
          </fieldset>
        )}

        {/* Gangschaltung Filter */}
        {filterOptions.transmissions.length > 0 && (
          <fieldset className="orm-filter-group">
            <legend>Gangschaltung</legend>
            {filterOptions.transmissions.map((transmission) => (
              <label key={transmission} className="orm-filter-option">
                <input
                  type="checkbox"
                  checked={filters.transmission.includes(transmission)}
                  onChange={(e) =>
                    handleFilterChange(
                      "transmission",
                      transmission,
                      e.target.checked,
                    )
                  }
                  aria-label={`Filter nach ${transmission} Schaltung`}
                />
                <span>{transmission}</span>
              </label>
            ))}
          </fieldset>
        )}

        {/* Personen Filter */}
        {filterOptions.persons.length > 0 && (
          <fieldset className="orm-filter-group">
            <legend>Personen</legend>
            {filterOptions.persons.map((persons) => (
              <label key={persons} className="orm-filter-option">
                <input
                  type="checkbox"
                  checked={filters.persons.includes(persons)}
                  onChange={(e) =>
                    handleFilterChange("persons", persons, e.target.checked)
                  }
                  aria-label={`Filter nach ${persons} Personen`}
                />
                <span>{persons} Personen</span>
              </label>
            ))}
          </fieldset>
        )}

        {/* Türen Filter */}
        {filterOptions.doors.length > 0 && (
          <fieldset className="orm-filter-group">
            <legend>Türen</legend>
            {filterOptions.doors.map((doors) => (
              <label key={doors} className="orm-filter-option">
                <input
                  type="checkbox"
                  checked={filters.doors.includes(doors)}
                  onChange={(e) =>
                    handleFilterChange("doors", doors, e.target.checked)
                  }
                  aria-label={`Filter nach ${doors} Türen`}
                />
                <span>{doors} Türen</span>
              </label>
            ))}
          </fieldset>
        )}
      </aside>

      {/* Fahrzeugliste */}
      <div className="orm-vehicleList">
        {filteredVehicles.length === 0 ? (
          <p className="orm-vehicleList-empty" role="status">
            Keine Fahrzeuge gefunden. Bitte passen Sie Ihre Filter an.
          </p>
        ) : (
          filteredVehicles.map((vehicle: any) => {
            const isSpecial = special === vehicle.id;
            return (
              <article
                className={`orm-vehicleList-car ${isSpecial ? "orm-vehicleList-car--special" : ""}`}
                key={vehicle.id}
                aria-label={`${vehicle.text_web} - ${vehicle.category_text}`}
              >
                <Slider {...settings}>
                  {vehicle.images.map((image: any, index: number) => (
                    <img
                      src={image.image}
                      key={image.image}
                      alt={`${vehicle.text_web} Bild ${index + 1}`}
                    />
                  ))}
                </Slider>
                <div className="orm-vehicleList-car-content">
                  <h3>{vehicle.text_web}</h3>
                  <p
                    className="orm-vehicleList-car-description"
                    dangerouslySetInnerHTML={{
                      __html: vehicle.description_web,
                    }}
                  ></p>
                  <p className="orm-vehicleList-car-category">
                    {vehicle.category_text}
                  </p>
                  {isSpecial && (
                    <span
                      className="orm-vehicleList-car-badge"
                      aria-label="Empfohlen"
                    >
                      Empfohlen
                    </span>
                  )}
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}

export default VehicleList;
